/*
 * Copyright (c) Nexign, JSC, 2020-2021.
 */
/**
 * Component to filter regions for tariffsAndServices
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @version 1.13.0
 * @since 1.4.0
 */

import { useCallback, useEffect, useState } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { icons } from '../../../assets';
import { Select } from '../../../components/common';
import { IOptionItemProps } from '../../../components/common/Select/Select';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationPageProducts,
} from '../../../modules/internationalization/intSelectors';
import ICommonTranslation from '../../../modules/internationalization/types/common';
import { IPageProductsTranslation } from '../../../modules/internationalization/types/pages/products';
import { modalOpen } from '../../../modules/modal/modalActions';
import { IModalOpenPayload } from '../../../modules/modal/types/modalActionTypes';
import { IModalRenderProps } from '../../../modules/modal/types/modalTypes';
import { IRootReducer } from '../../../reducers/rootReducer';
import { getTariffsAndServicesRegionListAction, productsSetRegionSelected } from '../tariffsAndServicesActions';
import {
    makeSelectProductsItemsSelectedCount,
    makeSelectProductsRegionIsDisabled,
    makeSelectProductsRegionParams,
} from '../tariffsAndServicesSelectors';
import {
    IProductsRegionItem,
    IProductsRegionParams,
    IProductsSetRegionSelectedPayload,
} from '../tariffsAndServicesTypes';
import ProductsChangeModal from './ProductsChangeModal';
import { TariffsAndServicesFilterRegionStyled } from './styled/TarriffsAndServicesFilterStyled';

export type TTariffsAndServicesFilterRegionProps = IConnectedProps & IDispatchProps;

export const ProductsFilterRegion = (props: TTariffsAndServicesFilterRegionProps) => {

    const {
        getRegionListAction,
        openModal,
        setRegionSelected,

        commonTranslation,
        itemsSelectedCount,
        productTranslation,
        regionIsDisabled,
        regionParams,
    } = props;

    const [searchValue, setSearchValue] = useState('');
    const [regionList, setRegionList] = useState<IProductsRegionItem[]>([]);

    useEffect(() => {
        setRegionList(regionParams.regionList);
    }, [regionParams.regionList]);

    const handleSearchChange = useCallback((searchValue: string) => {
        setSearchValue(searchValue);
        if (searchValue) {
            setRegionList(regionParams.regionList
                .filter(region => region.label.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1));
        } else {
            setRegionList(regionParams.regionList);
        }
    }, [regionParams.regionList, setSearchValue]);

    const handleSelectOptionSuccess = useCallback((selectedOption: IOptionItemProps) => {
        setRegionSelected({ regionSelected: selectedOption });
        if (searchValue) {
            getRegionListAction();
            setSearchValue('');
        }
    }, [getRegionListAction, setRegionSelected, searchValue]);

    const handleSelectOption = useCallback((selectedOption: IOptionItemProps) => {
        // if selected value is the same with current
        if (selectedOption.value === regionParams.regionSelected.value) return;
        // if no selected items
        if (itemsSelectedCount === 0) {
            handleSelectOptionSuccess(selectedOption);
            return;
        }

        openModal({
            render: (props: IModalRenderProps) => <ProductsChangeModal
                translation={productTranslation.regionChangeModal}
                onAgree={() => {
                    props.onCloseModal();
                    handleSelectOptionSuccess(selectedOption);
                }}
                onDisagree={() => props.onCloseModal()}
            />,
        });
    }, [
        handleSelectOptionSuccess,
        itemsSelectedCount,
        openModal,
        productTranslation.regionChangeModal,
        regionParams,
    ]);


    return (
        <TariffsAndServicesFilterRegionStyled>
            <Select
                disabled={regionIsDisabled}
                icon={icons.arrowListDownSmall}
                kind="normal"
                options={regionList}
                placeholder={''}
                searchProps={{
                    onSearchChange: handleSearchChange,
                    searchValue,
                }}
                selectedOption={regionParams.regionSelected}
                translation={commonTranslation}
                onSelect={handleSelectOption}
            />
        </TariffsAndServicesFilterRegionStyled>
    );
};

interface IConnectedProps {
    commonTranslation: ICommonTranslation;
    itemsSelectedCount: number;
    productTranslation: IPageProductsTranslation;
    regionIsDisabled: boolean;
    regionParams: IProductsRegionParams;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    commonTranslation: makeSelectTranslationCommon(),
    itemsSelectedCount: makeSelectProductsItemsSelectedCount(),
    productTranslation: makeSelectTranslationPageProducts(),
    regionIsDisabled: makeSelectProductsRegionIsDisabled(),
    regionParams: makeSelectProductsRegionParams(),
});

interface IDispatchProps {
    getRegionListAction: () => void;
    openModal: (payload: IModalOpenPayload) => void;
    setRegionSelected: (payload: IProductsSetRegionSelectedPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    getRegionListAction: () => dispatch(getTariffsAndServicesRegionListAction()),
    openModal: (payload: IModalOpenPayload) => dispatch(modalOpen(payload)),
    setRegionSelected: payload => dispatch(productsSetRegionSelected(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductsFilterRegion);
